import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const SERVER_URL = 'https://silent-uqpx.onrender.com';

function CreateRoom() {
  const [password, setPassword] = useState('');
  const [roomId, setRoomId] = useState('');
  const [roomCreated, setRoomCreated] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation('create');

  const createRoom = async () => {
    if (!password.trim()) {
      alert(t('errors.emptyPassword'));
      return;
    }
    try {
      const res = await axios.post(`${SERVER_URL}/new-room`, { password });
      setRoomId(res.data.roomId);
      setRoomCreated(true);
    } catch (err) {
      console.error(err);
      alert(t('errors.creationError'));
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${roomId}`);
    alert(t('copySuccess'));
  };

  if (!roomCreated) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <h2 style={styles.title}>{t('title')}</h2>
          <div style={styles.separator} />
          
          <input
            type="password"
            placeholder={t('passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            onKeyPress={(e) => e.key === 'Enter' && createRoom()}
          />

          <button 
            onClick={createRoom} 
            style={styles.mainButton}
          >
            {t('createButton')}
          </button>

          <p style={styles.securityNote}>
            {t('securityWarning')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <h2 style={styles.title}>{t('successTitle')}</h2>
        <div style={styles.separator} />

        <div style={styles.infoBox}>
          <p style={styles.infoLabel}>{t('roomId')}</p>
          <p style={styles.infoValue}>{roomId}</p>

          <p style={styles.infoLabel}>{t('shareLink')}</p>
          <div style={styles.linkContainer}>
            <code style={styles.link}>{`${window.location.origin}/join/${roomId}`}</code>
            <button 
              onClick={copyToClipboard} 
              style={styles.copyButton}
            >
              {t('copyButton')}
            </button>
          </div>

          <p style={styles.infoLabel}>{t('passwordLabel')}</p>
          <p style={styles.infoValue}>{password}</p>
        </div>

        <div style={styles.buttonGroup}>
          <button
            onClick={() => navigate(`/chat/${roomId}`, { state: { password } })}
            style={styles.mainButton}
          >
            {t('accessButton')}
          </button>

          <button 
            onClick={() => navigate('/')}
            style={styles.secondaryButton}
          >
            {t('backButton')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Les styles restent inchangés

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    padding: '2rem',
  },
  content: {
    width: '100%',
    maxWidth: '500px',
    textAlign: 'center',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#000',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: '1rem',
  },
  separator: {
    height: '2px',
    width: '60px',
    backgroundColor: '#000',
    margin: '1.5rem auto',
  },
  input: {
    width: '100%',
    padding: '1rem',
    marginBottom: '1.5rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    ':focus': {
      borderColor: '#000',
    },
  },
  mainButton: {
    width: '100%',
    padding: '1rem',
    backgroundColor: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    marginBottom: '1rem',
    ':hover': {
      opacity: 0.9,
    },
  },
  securityNote: {
    color: '#666',
    fontSize: '0.9rem',
    marginTop: '2rem',
  },
  infoBox: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '2rem',
    textAlign: 'left',
  },
  infoLabel: {
    color: '#666',
    fontSize: '0.9rem',
    margin: '0.5rem 0',
  },
  infoValue: {
    color: '#000',
    fontWeight: 600,
    marginBottom: '1.5rem',
  },
  linkContainer: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  link: {
    flex: 1,
    backgroundColor: '#fff',
    padding: '0.5rem',
    borderRadius: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.9rem',
  },
  copyButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#e9ecef',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#dee2e6',
    },
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#666',
    cursor: 'pointer',
    fontSize: '0.9rem',
    ':hover': {
      color: '#000',
    },
  },
};

export default CreateRoom;